import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, X, Truck, Phone, Edit2, ChevronDown, KeyRound } from 'lucide-react';

export default function DeliveryBoysPage() {
  const [boys, setBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetBoy, setResetBoy] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [editBoy, setEditBoy] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', password: '', area: '' });

  const fetchBoys = useCallback(async () => {
    try {
      const res = await api.getDeliveryBoys();
      setBoys(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBoys(); }, [fetchBoys]);

  const resetForm = () => setForm({ name: '', mobile: '', email: '', password: '', area: '' });

  const openNew = () => { resetForm(); setEditBoy(null); setShowModal(true); };
  const openEdit = (boy) => {
    setForm({ name: boy.name, mobile: boy.mobile, email: boy.email, password: '', area: boy.area || '' });
    setEditBoy(boy);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editBoy) {
        const update = { name: form.name, mobile: form.mobile, area: form.area, email: form.email };
        if (form.password) update.newPassword = form.password;
        await api.updateDeliveryBoy(editBoy._id, update);
      } else {
        await api.createDeliveryBoy(form);
      }
      setShowModal(false);
      fetchBoys();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error saving delivery boy');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delivery boy?')) return;
    await api.deleteDeliveryBoy(id);
    fetchBoys();
  };

  return (
    <div data-testid="delivery-boys-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Delivery Boys</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>{boys.length} delivery personnel</p>
        </div>
        <button data-testid="add-delivery-boy-button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Add Delivery Boy
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading...</div>
      ) : boys.length === 0 ? (
        <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
          <Truck className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
          <p>No delivery boys added yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {boys.map((boy, i) => (
            <div key={boy._id} className="rounded-xl p-5 transition-all duration-200 hover:-translate-y-1 fade-in" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', animationDelay: `${i * 50}ms` }}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>
                  {boy.name[0]?.toUpperCase()}
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-boy-${boy._id}`} onClick={() => openEdit(boy)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-boy-${boy._id}`} onClick={() => handleDelete(boy._id)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h4 className="font-semibold text-base" style={{ color: '#F5F5F4' }}>{boy.name}</h4>
              <div className="mt-2 space-y-1 text-sm" style={{ color: '#A8A29E' }}>
                <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" style={{ color: '#78716C' }} /> {boy.mobile}</p>
                <p className="text-xs">{boy.email}</p>
                {boy.area && <p className="text-xs">Area: {boy.area}</p>}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${boy.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                  {boy.status || 'active'}
                </span>
                <button data-testid={`reset-pwd-${boy._id}`} onClick={() => { setResetBoy(boy); setResetPassword(''); setShowResetModal(true); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all"
                  style={{ color: '#A8A29E', background: '#100E0C', border: '1px solid #2A241F' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#C8A96E'}
                  onMouseLeave={e => e.currentTarget.style.color = '#A8A29E'}>
                  <KeyRound className="w-3 h-3" /> Reset Pwd
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editBoy ? 'Edit Delivery Boy' : 'Add Delivery Boy'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Name *</label>
                <input data-testid="boy-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Mobile *</label>
                <input data-testid="boy-mobile" required value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              {!editBoy && (
                <>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Email *</label>
                    <input data-testid="boy-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Password *</label>
                    <input data-testid="boy-password" type="password" required value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                </>
              )}
              {editBoy && (
                <>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Email</label>
                    <input data-testid="boy-email-edit" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>New Password <span className="normal-case font-normal" style={{ color: '#78716C' }}>(leave blank to keep current)</span></label>
                    <input data-testid="boy-password-edit" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Enter new password" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Area</label>
                <input data-testid="boy-area" value={form.area} onChange={e => setForm({...form, area: e.target.value})} placeholder="e.g. Whitefield, Marathahalli" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="save-boy-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>
                  {editBoy ? 'Update' : 'Create Delivery Boy'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && resetBoy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-sm rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Reset Password</h3>
              <button onClick={() => setShowResetModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm" style={{ color: '#A8A29E' }}>Set new password for <strong style={{ color: '#F5F5F4' }}>{resetBoy.name}</strong></p>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>New Password *</label>
                <input data-testid="reset-new-password" type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} minLength={6} placeholder="Min 6 characters" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div className="flex gap-3">
                <button data-testid="reset-pwd-submit" onClick={async () => {
                  if (resetPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
                  try {
                    await api.adminResetPassword({ userId: resetBoy._id, newPassword: resetPassword });
                    alert(`Password reset for ${resetBoy.name}`);
                    setShowResetModal(false);
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to reset password');
                  }
                }} className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>Reset Password</button>
                <button onClick={() => setShowResetModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
